import React from "react";
import { useTranslation } from "react-i18next";

import { Box, Typography } from "@mui/material";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { ColumnDef } from "@tanstack/react-table";

type ActionCellProps = {
    workspace: API.Workspace;
    isDeleting: boolean;
    onEdit: (workspace: API.Workspace) => void;
    onDelete: (id: number) => void;
};

const ActionCell = ({ workspace, isDeleting, onEdit, onDelete }: ActionCellProps) => {
    const { t } = useTranslation(["common"]);

    return (
        <MinimalTableCell align="right">
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <SecondaryButton onClick={() => onEdit(workspace)} size="small" tooltipProps={{ title: t("common:button.edit") }}>
                    <i className="fa-solid fa-fw fa-pencil" />
                </SecondaryButton>
                <DangerOutlinedButton
                    onClick={() => onDelete(workspace.id)}
                    loading={isDeleting}
                    size="small"
                    tooltipProps={{ title: t("common:button.delete") }}
                >
                    <i className="fa-solid fa-fw fa-trash-alt" />
                </DangerOutlinedButton>
            </Box>
        </MinimalTableCell>
    );
};

type WorkspaceColumnActions = {
    onEdit: (workspace: API.Workspace) => void;
    onDelete: (id: number) => void;
    deletingId: number | null;
};

export const getWorkspaceColumns = (actions: WorkspaceColumnActions): ColumnDef<API.Workspace, string>[] => [
    {
        header: "ID",
        accessorKey: "id",
        cell: (info) => (
            <MinimalTableCell align="left">
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    },
    {
        header: "Name",
        accessorKey: "name",
        cell: (info) => (
            <MinimalTableCell align="left">
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    },
    {
        header: "GUID",
        accessorKey: "guid",
        cell: (info) => (
            <MinimalTableCell align="left">
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    },
    {
        header: "Organization",
        accessorKey: "organizationName",
        cell: (info) => (
            <MinimalTableCell align="left">
                <Typography>{info.getValue() || "-"}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    },
    {
        header: "",
        accessorKey: "actions",
        cell: (info) => (
            <ActionCell
                workspace={info.row.original}
                isDeleting={actions.deletingId === info.row.original.id}
                onEdit={actions.onEdit}
                onDelete={actions.onDelete}
            />
        ),
        enableSorting: false,
    },
];
